// src/Post.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
    Stack,
    CssBaseline,
    Typography,
    CircularProgress,
} from '@mui/material';
import Box from '@mui/joy/Box';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { useFeedLocation } from '../../hooks/useFeedLocation';
import { PostUI } from './PostUI';
import { CenteredFab } from '../../Components/Fab/CustomButton';
import { fetchPosts } from '../../services/CommonFunctions';
import { request } from '../../Utils/request';
import COLORS from '../../Utils/colors';


const Post: React.FC = () => {
    const navigate = useNavigate();
    const { postId } = useParams<{ postId?: string }>();
    const dispatch = useDispatch();
    const { posts: postData, currentPage, hasMore, loading } = useSelector((state: RootState) => state.post);
    const { postsReelsByLocation, locationForPosts, isLocationLoading } = useFeedLocation();
    const [sharedPost, setSharedPost] = useState<any | null>(null);
    const [sharedPostLoading, setSharedPostLoading] = useState(false);
    const [sharedPostNotFound, setSharedPostNotFound] = useState(false);
    const isSharePostView = Boolean(postId);

    const loadMorePosts = useCallback(() => {
        if (hasMore && !loading) {
            fetchPosts(dispatch, 'image', currentPage + 1, 5, true, postsReelsByLocation, locationForPosts);
        }
    }, [dispatch, currentPage, hasMore, loading, postsReelsByLocation, locationForPosts]);
    
    const handleScroll = useCallback(() => {
        if (
            window.innerHeight + document.documentElement.scrollTop >=
            document.documentElement.offsetHeight - 1000
        ) {
            loadMorePosts();
        }
    }, [loadMorePosts]);
    
    useEffect(() => {
        // Wait for location when "Posts by location" is on and we're still fetching it
        if (isSharePostView) return;
        if (postsReelsByLocation && isLocationLoading) return;
        fetchPosts(dispatch, 'image', 1, 10, false, postsReelsByLocation, locationForPosts);
    }, [dispatch, isSharePostView, postsReelsByLocation, locationForPosts, isLocationLoading]);

    useEffect(() => {
        if (isSharePostView) return;
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll, isSharePostView]);

    useEffect(() => {
        if (!postId) {
            setSharedPost(null);
            setSharedPostNotFound(false);
            setSharedPostLoading(false);
            return;
        }

        let cancelled = false;
        const getPostById = async () => {
            try {
                setSharedPostLoading(true);
                setSharedPostNotFound(false);
                const response = await request<any>(`/posts/${postId}`);
                if (cancelled) return;
                const data = response?.data;
                const onePost = Array.isArray(data) ? data[0] : data;
                if (onePost?._id || onePost?.id) {
                    setSharedPost(onePost);
                } else {
                    setSharedPost(null);
                    setSharedPostNotFound(true);
                }
            } catch (error) {
                if (!cancelled) {
                    setSharedPost(null);
                    setSharedPostNotFound(true);
                }
            } finally {
                if (!cancelled) setSharedPostLoading(false);
            }
        };

        getPostById();
        return () => {
            cancelled = true;
        };
    }, [postId]);


    return (
        <>
            <CssBaseline />
            <CenteredFab
                icon={<AddIcon />}
                label="Create Post"
                onClick={() => navigate("/createpost")}
                position="end"
                sx={{
                    display: isSharePostView ? 'none' : 'flex',
                    borderRadius:'12px',
                    backgroundColor: COLORS.primary,
                    color: 'white',
                    '&:hover': {
                        backgroundColor: COLORS.burntOrange,
                    }
                }}
            />

            <Box sx={{ p: 2, minHeight: '100vh', pb: '80px' }}>
                {isSharePostView ? (
                    <Stack spacing={4}>
                        {sharedPostLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <CircularProgress />
                            </Box>
                        )}
                        {!sharedPostLoading && sharedPost && (
                            <Box data-post-id={sharedPost._id || sharedPost.id}>
                                <PostUI item={sharedPost} />
                            </Box>
                        )}
                        {!sharedPostLoading && !sharedPost && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                                <Typography variant="h6">
                                    {sharedPostNotFound ? 'Post not found' : 'No post found'}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                ) : (
                    <>
                {postData.length > 0 ? (
                    <Stack spacing={4}>
                        {postData.map((item, index) => (
                            <Box key={index} data-post-id={item._id}>
                                <PostUI
                                    item={item}
                                />
                            </Box>
                        ))}
                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <CircularProgress />
                            </Box>
                        )}
                        {!hasMore && postData.length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    No more posts to load
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                        <Typography variant="h6">No posts found</Typography>
                    </Box>
                )}
                    </>
                )}
            </Box>
        </>
    );
};


export default Post;
